import React from "react";
import PropTypes from "prop-types";
import { compose } from "redux";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { Form, Field, reduxForm, isValid, isSubmitting } from "redux-form";
import ReCAPTCHA from "react-google-recaptcha";

import { ROUTES } from "../../../../../commons/routes/routes";
import AppLoading from "../../../../AppLayout/containers/AppLoading";
import SdsError from "../../../../../commons/containers/SdsError";
import SdsInput from "../../../../../commons/containers/SdsInput";
import SdsInputPassword from "../../../../../commons/containers/SdsInputPassword";
import SdsButton from "../../../../../commons/containers/SdsButton";
import RegionalField from "../../../../../commons/components/RegionalField";
import { obrigatorio } from "../../../../../commons/validation/validation";
import { maskDocumentoDinamico, stripDocumento } from "../../../../../utils/functions/maskDocumento";
import { IsEmpty } from "../../../../../utils/functions/IsEmpty";
import { imgHome } from "../../../../../utils/constants";
import AuthSignerDialog from "../../../Login/components/AuthSignerDialog";
import { ReCaptchaComponent } from "../../../../../commons/components/ReCaptcha/ReCaptchaComponent";
import { selectors as usuarioSelectors, types as usuarioTypes } from "../../../../../redux/modules/usuario";
import { selectors as flowSelectors } from "../../../../../redux/modules/flow";

import "../../../Login/containers/login-auth/login-auth-styles.css";
import "./login-whatsapp-auth-styles.css";

// redux-form (não react-hook-form) é usado propositalmente aqui: SdsInput/SdsInputPassword
// já são construídos para a API de redux-form (props input/meta), mesmo padrão de login-auth.jsx.
// Migrar exigiria adaptar/reescrever esses componentes compartilhados — risco desproporcional
// para esta tela.
const FORM_ID = "form_login_whatsapp";

// AuthSignerDialog só habilita a checagem da extensão de certificado digital
// quando "certificado-digital" está presente em tiposLoginAtivo. Nesta tela os
// 3 métodos são sempre exibidos, não dependem da config dinâmica de /login.
const TIPOS_LOGIN_ATIVO_FIXO = "certificado-digital";

class LoginWhatsappAuth extends ReCaptchaComponent {
  state = {
    identificacao: "",
    senha: "",
    mostrarLoginSistema: false,
  };

  handleInputChange = (event) => {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;

    this.setState({ [name]: value });
  };

  handleClickGov = () => {
    const { celular } = this.props;
    // Propaga o celular na querystring do redirect_uri: o oauth-server trata essa URL
    // como opaca e preserva a querystring adicional em todo o round-trip do Gov.br
    // (ver App.js, que relê "celular" de window.location.search no retorno).
    const celularQueryParam = celular ? `?celular=${encodeURIComponent(celular)}` : "";
    window.open(
      `/portal-externo-oauth-server/oauth2/authorize/govbr?redirect_uri=${window.location.origin}${process.env.PUBLIC_URL}/oauth2/redirect${celularQueryParam}`,
      "_self",
    );
  };

  handleClickLogin = () => {
    const senha = this.state.senha;
    const payload = {
      usuario: stripDocumento(this.state.identificacao),
      senha: IsEmpty(senha) ? senha : senha.toLowerCase(),
      redirectUrl: this.props.redirectUrl,
      captcha: this.getReCaptcha(),
      comCertificado: "",
    };
    this.props.login(payload);
    this.resetReCaptcha();
  };

  handleToggleLoginSistema = () => {
    this.setState((prevState) => ({
      mostrarLoginSistema: !prevState.mostrarLoginSistema,
    }));
  };

  render() {
    const {
      loading,
      login,
      valid,
      submitting,
      handleSubmit,
      captchaEnabled,
      captchaSitekey,
      data,
      done,
    } = this.props;

    const fnHandleSubmit = captchaEnabled ? this.validateReCaptcha : this.handleClickLogin;
    const error = (done && data && data.mensagemErro) ? data.mensagemErro : null;

    return (
      <section className="login-content login-whatsapp-root">
        <img src={imgHome} className="login-image-bg" alt="" />
        <section className="login-container" aria-label="Login WhatsApp">
          <div className="divisor" />
          <div className="main-area">
            <AppLoading show={loading} msg="LABELS.COMMONS.LOGIN.LOADING" />

            <h1 className="login-titulo-principal login-whatsapp-titulo">
              <FormattedMessage id="LABELS.COMMONS.NOME_CLIENTE" />
            </h1>

            <div className="sds-form-group descricao">
              <span>
                {/* "autenticar com o WhatsApp" em negrito (issue #67, achado de QA
                    manual) — mesmo padrão children-como-função usado no botão gov.br. */}
                <FormattedMessage id="LABELS.LOGIN_WHATSAPP.SUBTITULO">
                  {(text) => {
                    const destaque = "autenticar com o WhatsApp";
                    const idx = text.toLowerCase().indexOf(destaque.toLowerCase());
                    if (idx === -1) {
                      return text;
                    }
                    return (
                      <React.Fragment>
                        {text.slice(0, idx)}
                        <strong>{text.slice(idx, idx + destaque.length)}</strong>
                        {text.slice(idx + destaque.length)}
                      </React.Fragment>
                    );
                  }}
                </FormattedMessage>
              </span>
            </div>

            <div className="sds-form-group login-content">
              {/* Ícone de escudo/protegido + "gov.br" em negrito (issue #67, achado de QA
                  manual, igual ao protótipo) — texto vem de LABELS.COMMONS.LOGIN.GOVBR
                  ("Entrar com gov.br"), chave compartilhada com /login clássico; usamos o
                  padrão children-como-função do FormattedMessage (react-intl 2.x) pra
                  destacar só a marca "gov.br" sem duplicar/alterar a chave existente. */}
              <SdsButton type="button" onClick={this.handleClickGov}>
                <i className="material-icons sds-btn__icon" aria-hidden="true">verified_user</i>
                <FormattedMessage id="LABELS.COMMONS.LOGIN.GOVBR">
                  {(text) => {
                    const marca = "gov.br";
                    const idx = text.toLowerCase().indexOf(marca);
                    if (idx === -1) {
                      return text;
                    }
                    // "trimEnd" + espaco nao-quebravel via \u00A0 (issue #67, achado de
                    // QA manual: sem espaco visivel entre "Entrar com" e "gov.br") -- um
                    // espaco comum eh descartado aqui porque o container do botao eh
                    // "display: flex" (".sds-btn--full-width"): texto e <strong> viram
                    // "flex items" anonimos separados, e espaco em branco comum na borda
                    // de um flex item eh colapsado (diferente de fluxo inline normal).
                    // NBSP nao eh whitespace colapsavel pra esse algoritmo, entao
                    // sobrevive.
                    const NBSP = "\u00A0";
                    const antes = text.slice(0, idx).trimEnd();
                    const depois = text.slice(idx + marca.length);
                    return (
                      <React.Fragment>
                        {antes}
                        {NBSP}
                        <strong>{text.slice(idx, idx + marca.length)}</strong>
                        {depois}
                      </React.Fragment>
                    );
                  }}
                </FormattedMessage>
              </SdsButton>
            </div>
            <div className="sds-form-group descricao login-whatsapp-descricao">
              <span>
                <FormattedMessage id="LABELS.LOGIN_WHATSAPP.GOVBR_DESCRICAO" />
              </span>
            </div>

            <div className="hr-sect login-space">
              <FormattedMessage id="LABELS.COMMONS.OU" />
            </div>

            <div className="sds-form-group login-content">
              <AuthSignerDialog
                loading={loading}
                login={login}
                tiposLoginAtivo={TIPOS_LOGIN_ATIVO_FIXO}
                linkOnly={false}
                variant="whatsapp"
              />
            </div>
            <div className="sds-form-group descricao login-whatsapp-descricao">
              <span>
                <FormattedMessage id="LABELS.LOGIN_WHATSAPP.CERTIFICADO_DESCRICAO" />
              </span>
            </div>

            <div className="hr-sect login-space">
              <FormattedMessage id="LABELS.LOGIN_WHATSAPP.OU_SISTEMA" />
            </div>

            <div className="sds-form-group login-content">
              {/* variant="outline" (issue #67): só o gov.br permanece "cheio"/primário
                  no protótipo — certificado digital e login do sistema são opções
                  secundárias em destaque menor. Ícone "input" (seta clássica de entrar,
                  issue #67 achado de QA manual) — mesmo ícone usado no botão "Entrar" do
                  formulário CPF/senha logo abaixo. */}
              <SdsButton type="button" variant="outline" onClick={this.handleToggleLoginSistema}>
                <i className="material-icons sds-btn__icon" aria-hidden="true">input</i>
                <FormattedMessage id="LABEL.LOGIN.SISTEMA" />
              </SdsButton>
            </div>

            {this.state.mostrarLoginSistema && (
              <Form
                onSubmit={handleSubmit(fnHandleSubmit)}
                className="sds-input-format login-form-container"
              >
                <SdsError msg={error} />

                <RegionalField
                  campo="IDENTIFICACAO"
                  name="identificacao"
                  label="LABELS.COMMONS.USUARIO.IDENTIFICACAO"
                  type="text"
                  component={SdsInput}
                  value={this.state.identificacao}
                  onChange={this.handleInputChange}
                  validate={[obrigatorio]}
                  normalize={maskDocumentoDinamico}
                  format={maskDocumentoDinamico}
                  skipExtraValidate={true}
                  skipNormalize={true}
                  required={true}
                  autoComplete="off"
                />

                <Field
                  name="senha"
                  type="password"
                  component={SdsInputPassword}
                  value={this.state.senha}
                  onChange={this.handleInputChange}
                  label="LABELS.COMMONS.USUARIO.SENHA"
                  validate={[obrigatorio]}
                  required={true}
                />

                {captchaEnabled && (
                  <ReCAPTCHA
                    ref={this.recaptchaRef}
                    size={"invisible"}
                    sitekey={captchaSitekey}
                    onChange={this.handleClickLogin}
                  />
                )}

                <div className="sds-form-group sds-button-format">
                  {/* Ícone "input" (seta clássica de entrar, issue #67 achado de QA
                      manual). */}
                  <SdsButton type="submit" disabled={!valid || submitting}>
                    <i className="material-icons sds-btn__icon" aria-hidden="true">input</i>
                    <FormattedMessage id="LABELS.COMMONS.LOGIN.ENTRAR" />
                  </SdsButton>
                </div>

                {/* Esqueci a senha / Criar conta — presentes no protótipo (tela "Insira
                    sua senha") e ausentes na primeira versão desta tela (issue #67, achado
                    de QA manual). Reaproveita as mesmas chaves/rotas do login clássico
                    (login-auth.jsx). O texto de aceite de Termos/Política foi removido
                    (achado de QA: já existe na tela de criação de conta, não precisa
                    duplicar aqui). */}
                <div className="sds-row sds-center-xs">
                  <div className="sds-col-xs-12">
                    <Link
                      to={{
                        pathname: ROUTES.LOGIN_WHATSAPP_RECUPERAR_SENHA.path,
                        state: { identificacao: stripDocumento(this.state.identificacao) },
                      }}
                      className="sds-link"
                      aria-label="Recuperar Senha"
                    >
                      <FormattedMessage id="LABELS.COMMONS.LOGIN.ESQUECEU_A_SENHA" />
                    </Link>
                  </div>
                </div>

                <div className="sds-margin-vertical sds-row sds-center-xs">
                  <div className="sds-col-xs-12">
                    <p className="sds-p">
                      <FormattedMessage
                        id="LABELS.COMMONS.LOGIN.NAO_POSSUI_CONTA"
                        values={{
                          "route-to-criar-conta": (
                            <Link to={ROUTES.LOGIN_WHATSAPP_CRIAR_CONTA.path} className="sds-link" aria-label="Criar Conta">
                              <FormattedMessage id="LABELS.COMMONS.LOGIN.CRIAR_CONTA" />
                            </Link>
                          ),
                        }}
                      />
                    </p>
                  </div>
                </div>

              </Form>
            )}
          </div>
        </section>
      </section>
    );
  }
}

LoginWhatsappAuth.defaultProps = {
  redirectUrl: undefined,
  captchaEnabled: false,
  captchaSitekey: undefined,
  celular: null,
};

LoginWhatsappAuth.propTypes = {
  login: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  redirectUrl: PropTypes.string,
  captchaEnabled: PropTypes.bool,
  captchaSitekey: PropTypes.string,
  celular: PropTypes.string,
};

function mapStateToProps(state) {
  return {
    data: usuarioSelectors.getUser(state),
    done: flowSelectors.isDoneByType(state, usuarioTypes.USER_LOGIN),
    valid: isValid(FORM_ID)(state),
    submitting: isSubmitting(FORM_ID)(state),
  };
}

export default compose(
  connect(mapStateToProps),
  reduxForm({ form: FORM_ID }),
)(LoginWhatsappAuth);
